import { holdPropsInUtc, PushNotification } from "@repo/helpers";
import { FilterItem, KeyedArray } from "@repo/models";
import { useCallback } from "react";

import { useRepContext } from "../contexts/editableItemContext";
import { useSettingsQuery } from "../helpers/queryHooks";
import { RepDiscipline } from "../models";
import { EnumTable } from "../models/enumTable";
import { RepDisciplineHistory } from "../models/repDiscipline";
import * as Queries from "./queries";
import { useFilterItemV2QueryWrapper, useQueryWrapper, useRepPortalApi } from "./repPortalApi";

export function useRepDisciplineApi() {
	const { Get, Post, Delete } = useRepPortalApi();
	const [settings] = useSettingsQuery();

	const saveRepDiscipline = useCallback(
		function (rd: RepDiscipline) {
			const toSave = { ...rd };
			if (settings.timesInLocal) {
				holdPropsInUtc(toSave, "startDate", "endDate");
			}
			return Post<RepDiscipline>("SaveRepDiscipline", null, toSave);
		},
		[Post, settings.timesInLocal],
	);

	const getRepDisciplinesOnIncident = useCallback(
		async function (repId: number, incidentId: number) {
			return await Get<RepDiscipline>("GetRepDisciplinesOnIncident", {
				repId,
				incidentId,
			});
		},
		[Get],
	);

	const deleteSuspension = useCallback(
		async function (repDisciplineId: number) {
			return await Delete<boolean>("DeleteRepDiscipline", repDisciplineId);
		},
		[Delete],
	);

	const initialize = useCallback(function (discipline: RepDiscipline) {
		holdPropsInUtc(discipline, "startDate", "endDate");
		return discipline;
	}, []);

	const loadRepDiscipline = useCallback(
		function (repDisciplineId: RepDiscipline) {
			return Get<RepDiscipline>("GetRepDiscipline", { repDisciplineId }).then(initialize);
		},
		[Post, initialize],
	);

	const getRepDisciplines = useCallback(
		async function (repId: number) {
			return await Get<RepDiscipline[]>("RepDisciplines", { repId });
		},
		[Get],
	);

	const loadRepDisciplines = useCallback(
		function (repId: number) {
			return async function () {
				if (!repId) return defaultRepDisciplineArr();
				try {
					const disciplines = await getRepDisciplines(repId);
					const keyedItems = new KeyedArray<RepDiscipline>(
						disciplines.map(initialize),
						(b) => b.repDisciplineId,
					);
					return keyedItems;
				} catch (e) {
					PushNotification("Unable to retrieve disciplines");
					return defaultRepDisciplineArr();
				}
			};
		},
		[getRepDisciplines, initialize],
	);

	const getRepDisciplineHistories = useCallback(
		async function (repDisciplineId: number) {
			return await Get<RepDisciplineHistory[]>("RepDisciplineHistories", {
				repDisciplineId,
			});
		},
		[Get],
	);

	const loadRepDisciplineHistories = useCallback(
		function (repDisciplineId: number) {
			return async function () {
				if (!repDisciplineId) return defaultRepDisciplineHistoryArr();
				try {
					const disciplinesHistories = await getRepDisciplineHistories(repDisciplineId);
					const keyedItems = new KeyedArray<RepDisciplineHistory>(
						disciplinesHistories,
						(b) => b.repDisciplineHistoryId,
					);
					return keyedItems;
				} catch (e) {
					PushNotification("Unable to retrieve discipline histories");
					return defaultRepDisciplineHistoryArr();
				}
			};
		},
		[getRepDisciplineHistories],
	);

	const getDisciplineTrainings = useCallback(
		function (repId: number, mfEnabled: boolean) {
			return async function () {
				if (!mfEnabled) return [];
				return await Get<FilterItem[]>("GetDisciplineTrainings", { repId });
			};
		},
		[Get],
	);

	const getRepFeatures = useCallback(() => async () => Get<EnumTable[]>("GetActiveRepFeatures"), [Get]);

	return {
		saveRepDiscipline,
		loadRepDiscipline,
		getRepDisciplinesOnIncident,
		deleteSuspension,
		loadRepDisciplineHistories,
		getRepFeatures,
		loadRepDisciplines,
		getDisciplineTrainings,
	};
}

export function useRepDisciplineQueries() {
	const { repId } = useRepContext();
	const { loadRepDisciplines, loadRepDisciplineHistories, getDisciplineTrainings, getRepFeatures } =
		useRepDisciplineApi();

	const useRepDisciplineQuery = () =>
		useQueryWrapper(Queries.REP_DISCIPLINES, [repId, loadRepDisciplines], loadRepDisciplines(repId));

	const useRepDisciplineHistoryQuery = (repDisciplineId: number) =>
		useQueryWrapper(
			Queries.REP_DISCIPLINES_HISTORY,
			[repDisciplineId, loadRepDisciplineHistories],
			loadRepDisciplineHistories(repDisciplineId),
		);

	const useTrainingsQuery = (mfEnabled: boolean) =>
		useFilterItemV2QueryWrapper(
			Queries.REP_DISCIPLINES_TRAININGS,
			[repId, mfEnabled, getDisciplineTrainings],
			getDisciplineTrainings(repId, mfEnabled),
		);

	const useRepFeaturesQuery = () => useQueryWrapper(Queries.REP_FEATURES, [getRepFeatures], getRepFeatures());

	return {
		useRepDisciplineQuery,
		useRepDisciplineHistoryQuery,
		useTrainingsQuery,
		useRepFeaturesQuery,
	};
}

const defaultRepDisciplineArr = () => new KeyedArray<RepDiscipline>([], (b) => b.repDisciplineId);
const defaultRepDisciplineHistoryArr = () => new KeyedArray<RepDisciplineHistory>([], (b) => b.repDisciplineHistoryId);
